import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import '../../styles/feedback.css';

/**
 * Success message component shown after feedback submission
 * @param {Object} props - Component props
 * @param {string} [props.submissionId] - Unique submission ID
 * @param {Object} [props.metaIssue] - Meta repo issue info { number, url }
 * @param {Object} [props.projectIssue] - Project repo issue info { number, url, repo }
 * @param {string} [props.message] - Custom success message
 * @param {Function} [props.onSubmitAnother] - Callback to submit another feedback
 * @returns {JSX.Element} FeedbackSuccess component
 */
const FeedbackSuccess = ({
  submissionId,
  metaIssue,
  projectIssue,
  message,
  onSubmitAnother
}) => {
  const location = useLocation();

  return (
    <div className="feedback-success">
      <div className="feedback-success-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      </div>

      <h2 className="feedback-success-title">Thank You!</h2>

      <p className="feedback-success-message">
        {message || 'Your feedback has been submitted successfully. We appreciate you taking the time to help us improve.'}
      </p>

      {submissionId && (
        <div className="feedback-submission-id">
          <span className="label">Reference ID:</span>
          <code>{submissionId}</code>
        </div>
      )}

      {metaIssue && (
        <div className="feedback-issue-link">
          <span className="label">Tracking Issue:</span>
          <a
            href={metaIssue.url}
            target="_blank"
            rel="noopener noreferrer"
            className="issue-link"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            Issue #{metaIssue.number}
          </a>
        </div>
      )}

      {projectIssue && (
        <div className="feedback-issue-link secondary">
          <span className="label">Development Issue:</span>
          <a
            href={projectIssue.url}
            target="_blank"
            rel="noopener noreferrer"
            className="issue-link"
          >
            {projectIssue.repo.split('/')[1]} #{projectIssue.number}
          </a>
        </div>
      )}

      <div className="feedback-success-actions">
        {onSubmitAnother && (
          <button
            type="button"
            className="feedback-submit-another"
            onClick={onSubmitAnother}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Submit Another
          </button>
        )}

        <Link
          to={`/${location.search}`}
          className="feedback-back-home"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          Back to Home
        </Link>
      </div>

      <div className="feedback-success-footer">
        <p>
          Your feedback helps us improve the platform for everyone.
          We'll review your input and take appropriate action.
        </p>
      </div>
    </div>
  );
};

export default FeedbackSuccess;
